/**
 * OperationsAttendancePage - FM Site Attendance Dashboard
 * =======================================================
 * Shows which contractors are attending which sites today.
 */
import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL || "";

export default function OperationsAttendancePage() {
  const [loading, setLoading] = useState(true);
  const [attendanceData, setAttendanceData] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [expandedSite, setExpandedSite] = useState(null);

  const loadAttendance = useCallback(async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API}/api/operations/attendance?date=${selectedDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.data.ok) {
        setAttendanceData(res.data);
      } else {
        toast.error("Failed to load attendance data");
      }
    } catch (err) {
      console.error("Load attendance error:", err);
      toast.error(err.response?.data?.detail || "Failed to load attendance");
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  useEffect(() => {
    loadAttendance();
  }, [loadAttendance]);

  const openInMaps = (site) => {
    const query = encodeURIComponent(`${site.site_name || ''}, ${site.postcode || ''}`);
    window.open(`https://www.google.com/maps/search/?api=1&query=${query}`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <svg className="animate-spin h-10 w-10 text-teal-600 mx-auto mb-4" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          <p className="text-slate-600">Loading attendance...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-8 space-y-6">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Site Attendance</h1>
            <p className="text-slate-500">Who's on which site today</p>
          </div>
          <div className="flex items-center gap-4">
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-4 py-2 rounded-xl border border-slate-200 focus:border-teal-500 focus:ring-2 focus:ring-teal-500/20 outline-none"
            />
            <Button onClick={loadAttendance} variant="outline">Refresh</Button>
          </div>
        </div>

        {/* Summary KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-teal-600">
                {attendanceData?.total_sites_with_activity || 0}
              </div>
              <p className="text-sm text-slate-500">Sites with Activity</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-blue-600">
                {attendanceData?.total_contractors_attending || 0}
              </div>
              <p className="text-sm text-slate-500">Contractors Attending</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-amber-600">
                {attendanceData?.total_jobs_scheduled || 0}
              </div>
              <p className="text-sm text-slate-500">Jobs Scheduled</p>
            </CardContent>
          </Card>
        </div>

        {/* Sites List */}
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-slate-900">Sites Today</h2>
          
          {attendanceData?.sites?.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <p className="text-slate-500">No site activity scheduled for this date</p>
              </CardContent>
            </Card>
          ) : (
            attendanceData?.sites?.map((site) => (
              <Card key={site.site_key} className="overflow-hidden">
                <CardHeader 
                  className="cursor-pointer hover:bg-slate-50 transition-colors"
                  onClick={() => setExpandedSite(
                    expandedSite === site.site_key ? null : site.site_key
                  )}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="bg-blue-100 text-blue-700 font-bold rounded-xl w-12 h-12 flex items-center justify-center">
                        {site.job_count}
                      </div>
                      <div>
                        <CardTitle className="text-lg">{site.site_name}</CardTitle>
                        <p className="text-sm text-slate-500">{site.postcode}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {site.open_compliance_cases > 0 && (
                        <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-xs font-semibold">
                          {site.open_compliance_cases} compliance issues
                        </span>
                      )}
                      <div className="flex -space-x-2">
                        {site.contractors?.slice(0, 3).map((contractor, idx) => (
                          <div 
                            key={idx}
                            className="bg-teal-500 text-white text-xs font-bold rounded-full w-8 h-8 flex items-center justify-center border-2 border-white"
                            title={contractor.name}
                          >
                            {contractor.name?.charAt(0) || '?'}
                          </div>
                        ))}
                        {site.contractor_count > 3 && (
                          <div className="bg-slate-300 text-slate-600 text-xs font-bold rounded-full w-8 h-8 flex items-center justify-center border-2 border-white">
                            +{site.contractor_count - 3}
                          </div>
                        )}
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={(e) => { e.stopPropagation(); openInMaps(site); }}
                      >
                        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                        </svg>
                      </Button>
                      <svg 
                        className={`h-5 w-5 text-slate-400 transition-transform ${
                          expandedSite === site.site_key ? 'rotate-180' : ''
                        }`}
                        fill="none" viewBox="0 0 24 24" stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </CardHeader>
                
                {expandedSite === site.site_key && (
                  <CardContent className="border-t bg-slate-50 space-y-4">
                    {/* Contractors */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Contractors Attending</h4>
                      <div className="flex flex-wrap gap-2">
                        {site.contractors?.map((contractor, idx) => (
                          <span 
                            key={idx}
                            className="bg-teal-100 text-teal-700 px-3 py-1 rounded-full text-sm font-medium"
                          >
                            {contractor.name}
                          </span>
                        ))}
                      </div>
                    </div>

                    {/* Jobs */}
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">Jobs</h4>
                      <div className="space-y-2">
                        {site.jobs?.map((job, idx) => (
                          <div 
                            key={idx}
                            className="flex items-center justify-between bg-white rounded-xl p-3 border border-slate-200"
                          >
                            <div>
                              <p className="font-medium text-slate-800">{job.job_reference}</p>
                              <p className="text-xs text-slate-500">
                                {job.scheduled_date ? `Scheduled: ${new Date(job.scheduled_date).toLocaleTimeString()}` : 'No time set'}
                              </p>
                            </div>
                            <span className={`px-2 py-1 rounded text-xs font-medium ${
                              job.status === 'Completed' ? 'bg-green-100 text-green-700' :
                              job.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {job.status}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* First Arrival */}
                    {site.first_scheduled_time && (
                      <div className="text-sm text-slate-500">
                        First scheduled arrival: {new Date(site.first_scheduled_time).toLocaleTimeString()}
                      </div>
                    )}
                  </CardContent>
                )}
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
